import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellOff, BellRing, Loader2, Send, Settings2, Smartphone, Vibrate } from 'lucide-react';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import PushNotificationService from '../utils/pushNotifications';
import { haptic } from '../utils/vibration';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import ModalShell from './ModalShell';
import WindowModalHeader from './WindowModalHeader';

const primaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40';
const secondaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800';

const getNotificationPermission = () => {
  if (typeof Notification === 'undefined' || !Notification.permission) return 'unsupported';
  return Notification.permission;
};

const getPermissionMeta = (permission) => {
  if (permission === 'granted') {
    return { label: 'Разрешены', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/35 dark:text-emerald-300' };
  }
  if (permission === 'denied') {
    return { label: 'Заблокированы', className: 'bg-red-50 text-red-700 dark:bg-red-950/35 dark:text-red-300' };
  }
  if (permission === 'default') {
    return { label: 'Не запрошены', className: 'bg-amber-50 text-amber-800 dark:bg-amber-950/35 dark:text-amber-200' };
  }
  return { label: 'Недоступны', className: 'bg-red-50 text-red-700 dark:bg-red-950/35 dark:text-red-300' };
};

const isPushSupported = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return new PushNotificationService().isSupported();
};

const ToggleRow = ({ icon: Icon, title, description, active, onClick, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${
      active
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
        : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
    } disabled:cursor-not-allowed disabled:opacity-55`}
  >
    <Icon className={`h-5 w-5 shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
      <span className="mt-1 block text-xs leading-relaxed text-gray-500 dark:text-gray-400">{description}</span>
    </span>
    <span
      aria-hidden="true"
      className="toggle-switch shrink-0"
      style={{
        '--toggle-bg': active ? 'rgb(59 130 246)' : 'rgb(156 163 175)',
        '--toggle-height': '24px',
        '--toggle-width': '40px',
        '--knob-x': active ? '18px' : '2px',
      }}
    >
      <span className="toggle-switch-knob" />
    </span>
  </button>
);

export const NotificationSettingsControl = () => {
  const {
    pushVibrationEnabled,
    setPushVibrationEnabled,
    browserNotificationsEnabled,
    setBrowserNotificationsEnabled,
  } = useInteractionPreferences();
  const [modalOpen, setModalOpen] = useState(false);
  const [pushSupported, setPushSupported] = useState(isPushSupported);
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [permission, setPermission] = useState(getNotificationPermission);
  const [action, setAction] = useState('');
  const serviceRef = useRef(null);
  const panelRef = useRef(null);
  const { toast, showToast, hideToast } = useToast(3500);
  const permissionMeta = getPermissionMeta(permission);

  const getService = useCallback(() => {
    if (!serviceRef.current) serviceRef.current = new PushNotificationService();
    return serviceRef.current;
  }, []);

  const refreshSubscription = useCallback(async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    setPermission(getNotificationPermission());
    if (!supported) {
      setIsPushSubscribed(false);
      return;
    }

    try {
      const subscription = await getService().getSubscription();
      setIsPushSubscribed(Boolean(subscription));
    } catch {
      setIsPushSubscribed(false);
    }
  }, [getService]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  useModalBackButton(modalOpen, () => setModalOpen(false));

  useEffect(() => {
    if (!modalOpen) return undefined;
    const previous = document.activeElement;
    const frame = requestAnimationFrame(() => panelRef.current?.querySelector('button:not(:disabled)')?.focus());
    const keepFocusInside = (event) => {
      if (event.key !== 'Tab') return;
      const controls = panelRef.current?.querySelectorAll('button:not(:disabled), [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keepFocusInside);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', keepFocusInside);
      previous?.focus?.();
    };
  }, [modalOpen]);

  const handlePushSubscription = async () => {
    if (!pushSupported || action) return;
    haptic('selection');
    setAction('subscription');

    try {
      const service = getService();
      if (isPushSubscribed) {
        await service.unsubscribe();
        setIsPushSubscribed(false);
        showToast('Push-уведомления отключены', 'success');
      } else {
        if (getNotificationPermission() === 'denied') {
          setPermission('denied');
          throw new Error('Уведомления заблокированы в браузере. Разрешите их в настройках браузера.');
        }
        await service.subscribeToPush();
        setPermission(getNotificationPermission());
        setIsPushSubscribed(true);
        haptic('success');
        showToast('Push-уведомления включены', 'success');
      }
    } catch (error) {
      showToast(error.message || 'Не удалось изменить подписку', 'error');
      await refreshSubscription();
    } finally {
      setAction('');
    }
  };

  const handleTestPush = async () => {
    if (!isPushSubscribed || action) return;
    haptic('selection');
    setAction('test');
    try {
      await getService().sendTestNotification();
      haptic('success');
      showToast('Тестовое уведомление отправлено', 'success');
    } catch (error) {
      showToast(error.message || 'Не удалось отправить тестовое уведомление', 'error');
      await refreshSubscription();
    } finally {
      setAction('');
    }
  };

  const toggleBrowserNotifications = () => {
    if (permission !== 'granted') return;
    haptic('selection');
    setBrowserNotificationsEnabled(!browserNotificationsEnabled);
  };

  const togglePushVibration = () => {
    haptic('selection');
    setPushVibrationEnabled(!pushVibrationEnabled);
  };

  const closeModal = () => setModalOpen(false);
  const subscriptionLabel = !pushSupported
    ? 'Недоступны'
    : isPushSubscribed ? 'Подписка активна' : 'Не подключены';
  const subscriptionClass = isPushSubscribed
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/35 dark:text-emerald-300'
    : pushSupported
      ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
      : 'bg-red-50 text-red-700 dark:bg-red-950/35 dark:text-red-300';

  return <>
    <div className={`flex items-center rounded-xl border transition-colors ${isPushSubscribed ? 'border-blue-500 bg-blue-50 shadow-[0_6px_18px_rgba(59,130,246,0.12)] dark:bg-blue-950/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}>
      <button
        type="button"
        aria-label="Переключить push-уведомления"
        disabled={!pushSupported || Boolean(action)}
        onClick={handlePushSubscription}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-3.5 py-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isPushSubscribed ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
          {action === 'subscription' ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : isPushSubscribed ? <BellRing className="h-[18px] w-[18px]" /> : <BellOff className="h-[18px] w-[18px]" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-snug text-gray-900 dark:text-gray-100">Уведомления</span>
          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{subscriptionLabel}</span>
        </span>
      </button>
      <button
        type="button"
        disabled={!pushSupported || Boolean(action)}
        aria-disabled={!pushSupported || Boolean(action)}
        onClick={() => { haptic('selection'); setModalOpen(true); }}
        aria-label="Настройки уведомлений"
        title="Настройки уведомлений"
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors disabled:cursor-not-allowed disabled:opacity-45 text-blue-600 hover:bg-white/80 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-gray-700"
      >
        <Settings2 className="h-[18px] w-[18px]" />
      </button>
      <button
        type="button"
        role="switch"
        aria-checked={isPushSubscribed}
        aria-label="Push-уведомления"
        disabled={!pushSupported || Boolean(action)}
        onClick={handlePushSubscription}
        className="mr-3 inline-flex shrink-0 items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span aria-hidden="true" className="toggle-switch" style={{ '--toggle-bg': isPushSubscribed ? 'rgb(59 130 246)' : 'rgb(156 163 175)', '--toggle-height': '26px', '--toggle-width': '44px', '--knob-x': isPushSubscribed ? '20px' : '2px' }}><span className="toggle-switch-knob" /></span>
      </button>
    </div>

    <ModalShell open={modalOpen} onClose={closeModal} variant="window" panelClassName="flex max-h-[85vh] max-w-md flex-col overflow-hidden !p-0" panelProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'notification-settings-modal-title' }}>
      <div ref={panelRef} className="flex min-h-0 flex-col">
        <WindowModalHeader icon={Bell} title="Настройки уведомлений" titleId="notification-settings-modal-title" onClose={closeModal} closeLabel="Закрыть настройки уведомлений" />
        <div className="min-h-0 space-y-5 overflow-y-auto overscroll-contain p-5">
          <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">Настройки применяются на этом устройстве. Push приходят, когда приложение закрыто, а системные уведомления — пока оно открыто.</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Push</div>
              <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${subscriptionClass}`}>{subscriptionLabel}</span>
            </div>
            <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Разрешение</div>
              <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${permissionMeta.className}`}>{permissionMeta.label}</span>
            </div>
          </div>

          {pushSupported ? <ToggleRow
            icon={isPushSubscribed ? BellRing : BellOff}
            title="Push-уведомления"
            description="Получать сообщения, когда приложение закрыто или свёрнуто."
            active={isPushSubscribed}
            disabled={Boolean(action)}
            onClick={handlePushSubscription}
          /> : <div className="rounded-xl bg-red-50 p-3 text-sm leading-relaxed text-red-700 dark:bg-red-950/35 dark:text-red-300">Push-уведомления не поддерживаются этим браузером или доступны только по защищённому соединению.</div>}

          <ToggleRow
            icon={Smartphone}
            title="Системные уведомления в приложении"
            description={permission === 'granted' ? 'Показывать уведомление браузера сразу после нового сообщения.' : 'Станет доступно после разрешения уведомлений в браузере.'}
            active={browserNotificationsEnabled}
            disabled={permission !== 'granted'}
            onClick={toggleBrowserNotifications}
          />

          <ToggleRow
            icon={Vibrate}
            title="Вибрация"
            description="Вибрировать при push и системных уведомлениях, если устройство это поддерживает."
            active={pushVibrationEnabled}
            onClick={togglePushVibration}
          />

          <div className="rounded-xl border border-dashed border-gray-200 p-3.5 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-700 dark:bg-purple-950/35 dark:text-purple-300"><Send className="h-[18px] w-[18px]" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Проверка push</div>
                <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">Отправит тестовое уведомление на это устройство.</p>
              </div>
            </div>
            <button type="button" disabled={!isPushSubscribed || Boolean(action)} onClick={handleTestPush} className={`${secondaryButton} mt-3 w-full`}>
              {action === 'test' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {action === 'test' ? 'Отправляем…' : 'Отправить тестовое уведомление'}
            </button>
            {!isPushSubscribed && <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Сначала включите push-уведомления.</p>}
          </div>
        </div>
        <div className="flex shrink-0 border-t border-gray-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-800">
          <button type="button" onClick={closeModal} className={`${primaryButton} w-full`}>Готово</button>
        </div>
      </div>
    </ModalShell>
      </>;
};
